// The outfit-picking page a player lands on after scanning the game's
// sign-up link, or a team's door code before they have an outfit (as opposed
// to a per-slot code, which still claims a fixed outfit straight away - see
// JoinFromQueryParams). They declare what they own, are offered a ranked list
// of outfits that are both wearable and distinguishable from everyone already
// placed, pick one, confirm it, and lock it in. A game code puts them in the
// game with no team - the team is scanned in at the door on the night
// (roadmap R15) - while a team code joins the team as part of the pick. See
// docs/roadmap.md's #10 entry and backend/identity_admin.py's outfit_options
// for the ranking rationale behind the flow below.
//
// Deliberately outside UserMode: no map, no webcam, no SSE, no permission
// polling while picking - this page only ever talks to join_options /
// outfit_options / pick_outfit. The one exception is after the outfit is
// locked in, where there are two ways on to the game proper: a lightweight
// user_info poll (see PickOutfit's own effect) hard-redirects to / once the
// game goes active, since nothing else on this page would ever tell a player
// who left the tab open that it had started, and ResultScreen's own button
// goes there on demand - / is where the camera and location permissions are
// granted, so it is somewhere worth being before the game starts too.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Popup from "./Popup";
import { sendAPIRequest } from "./utils";
import { usePatchSearchParams } from "./urlState";
import { NameEntry } from "./OnboardingView";
import { Swatch } from "./Swatch";
import CuriosityFooter from "./CuriosityFooter";
import prose from "./prose";

import styles from "./PickOutfit.module.css";

const p = prose.pickOutfit;

// Same idiom as JoinFromQueryParams.useQuery - this page is mounted at its
// own flat route, not underneath it, so it needs its own copy.
function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// A channel name is only ever a raw scheme identifier ("tshirt", "hat", ...)
// - this is the one place that turns it into something worth showing a
// player. Keyed off the name so a future channel (a "shape" channel, say)
// just falls through to the capitalised default instead of reading raw.
function channelLabel(name) {
  return (
    p.channelDisplayNames[name] || name.charAt(0).toUpperCase() + name.slice(1)
  );
}

async function getJSON(endpoint, query_params) {
  const response = await sendAPIRequest(endpoint, query_params, "GET");
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  if (!response.ok) {
    const detail = data && data.detail;
    const err = new Error(
      typeof detail === "string" ? detail : JSON.stringify(detail || data),
    );
    err.status = response.status;
    throw err;
  }
  return data;
}

async function postJSON(endpoint, body) {
  const response = await sendAPIRequest(endpoint, null, "POST", null, body);
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  if (!response.ok) {
    const detail = data && data.detail;
    const err = new Error(
      typeof detail === "string" ? detail : JSON.stringify(detail || data),
    );
    err.status = response.status;
    throw err;
  }
  return data;
}

function Header({ joinData, showWardrobePrompt }) {
  return (
    <div className={styles.header}>
      {/* The backend has already swapped this whole block to the team the
          player is actually in, so the link they tapped is never named. */}
      {joinData.joined_other_team ? (
        <p className={styles.alreadyJoinedNote}>{p.alreadyJoinedNote}</p>
      ) : null}
      {/* A game code names no team: the sign-up link is the same for
          everybody, and teams are dealt at the door. */}
      <h1>
        {joinData.team_name
          ? p.teamHeading(joinData.team_name)
          : p.signUpHeading}
      </h1>
      {showWardrobePrompt ? <p>{p.wardrobePrompt}</p> : null}
    </div>
  );
}

// Notes ride on the channel, not on the page: the channels do not share a
// colour vocabulary, and where they use the same word they can mean different
// things by it (charcoal is "black" on the legs and not on a top).
function WardrobeChannel({ channel, selected, onToggle }) {
  return (
    <fieldset className={styles.wardrobeChannel}>
      <legend>{channelLabel(channel.name)}</legend>
      <div className={styles.swatchGrid}>
        {channel.labels.map((label) => {
          const isSelected = selected.includes(label);
          const note = (channel.notes || {})[label];
          return (
            <button
              type="button"
              key={label}
              className={
                styles.swatchButton +
                (isSelected ? " " + styles.swatchSelected : "")
              }
              aria-pressed={isSelected}
              aria-label={label}
              onClick={() => onToggle(label)}
            >
              {/* A checkmark, not just a highlighted border - ticking is
                  multi-select, and a border-only highlight reads as the
                  single-choice picker this deliberately isn't. */}
              <span className={styles.swatchCheckbox} aria-hidden="true">
                {isSelected ? "✓" : ""}
              </span>
              <Swatch hex={channel.hex[label]} label={label} size="large" />
              <span className={styles.swatchLabel}>{label}</span>
              {note ? <span className={styles.swatchNote}>{note}</span> : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// Both used to key/label an option by what the player can actually see and
// chose: the wardrobe channels only (roadmap #10 revision) - the hat is
// pinned to the team colour and the armband is ours to assign, so neither is
// a choice the player has a stake in, and the backend now collapses options
// so no two share a wardrobe combination, keeping this a stable React key.
function optionDescription(option, wardrobeChannels) {
  return wardrobeChannels
    .map((name) => `${channelLabel(name)} ${option.appearance[name]}`)
    .join(", ");
}

function OutfitGarments({ appearance, wardrobeChannels, channels, size }) {
  return (
    <div className={styles.optionGarments}>
      {wardrobeChannels.map((name) => {
        const channel = channels.find((c) => c.name === name);
        const label = appearance[name];
        return (
          <span key={name} className={styles.garment}>
            <Swatch hex={channel.hex[label]} label={label} size={size} />
            <span className={styles.garmentLabel}>
              {channelLabel(name)}: {label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

// The "recommended" badge marks the best outfit available, not merely a
// canonical one: the backend ranks by (overrides, -rarity, ...), so that is
// the head of the list, plus anything immediately behind it tying on rarity
// - a tie is genuinely a tie, and singling one out would be arbitrary. The
// rest of the canonical options are perfectly good and simply go unbadged;
// only the head of the *first* page is the best, so later pages badge
// nothing. Rarity is a sum of floats, hence the tolerance.
const RARITY_TOLERANCE = 1e-9;

function bestOptions(options) {
  const best = options[0];
  if (!best || !best.is_canonical) return [];
  const tied = [];
  for (const option of options) {
    if (
      !option.is_canonical ||
      Math.abs(option.rarity - best.rarity) >= RARITY_TOLERANCE
    )
      break;
    tied.push(option);
  }
  return tied;
}

function OptionRow({
  option,
  recommended,
  wardrobeChannels,
  channels,
  onPick,
}) {
  return (
    <button
      type="button"
      className={styles.optionRow}
      aria-label={p.chooseAriaLabel(
        optionDescription(option, wardrobeChannels),
      )}
      onClick={() => onPick(option)}
    >
      {recommended ? (
        <span className={styles.recommendedBadge}>{p.recommendedBadge}</span>
      ) : null}
      {option.is_canonical ? null : (
        <span className={styles.notIdealBadge}>{p.notIdealBadge}</span>
      )}
      <OutfitGarments
        appearance={option.appearance}
        wardrobeChannels={wardrobeChannels}
        channels={channels}
      />
    </button>
  );
}

// Options arrive from the backend already sorted by overrides_needed, so a
// simple linear scan groups consecutive equal values without re-sorting.
function OptionsList({
  options,
  recommendedKeys,
  wardrobeChannels,
  channels,
  onPick,
}) {
  let lastOverrides = null;
  return (
    <div className={styles.optionsList}>
      {options.map((option) => {
        const showHeading = option.overrides_needed !== lastOverrides;
        lastOverrides = option.overrides_needed;
        const key = optionDescription(option, wardrobeChannels);
        return (
          <React.Fragment key={key}>
            {showHeading ? (
              <h3 className={styles.groupHeading}>
                {option.overrides_needed === 0
                  ? p.exactMatchHeading
                  : p.coloursDifferentHeading(option.overrides_needed)}
              </h3>
            ) : null}
            <OptionRow
              option={option}
              recommended={recommendedKeys.has(key)}
              wardrobeChannels={wardrobeChannels}
              channels={channels}
              onPick={onPick}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function WardrobeSummary({ wardrobeChannels, wardrobe, onChange }) {
  const description = wardrobeChannels
    .map((name) => {
      const chosen = wardrobe[name] || [];
      return `${channelLabel(name)}: ${chosen.length ? chosen.join(", ") : p.wardrobeAnythingFallback}`;
    })
    .join(" · ");
  return (
    <div className={styles.wardrobeSummary}>
      <p>{description}</p>
      <button type="button" className={styles.linkButton} onClick={onChange}>
        {p.changeWhatIOwnButton}
      </button>
    </div>
  );
}

function ConfirmScreen({
  option,
  wardrobeChannels,
  channels,
  onConfirm,
  onBack,
  confirming,
  hasName,
  checked,
  onCheckedChange,
}) {
  return (
    <div className={styles.confirmScreen}>
      <h2>{p.confirmHeading}</h2>
      <p className={styles.confirmIntro}>{p.confirmIntro()}</p>
      <OutfitGarments
        appearance={option.appearance}
        wardrobeChannels={wardrobeChannels}
        channels={channels}
        size="large"
      />
      <label
        className={
          styles.confirmRow +
          " " +
          (checked ? styles.confirmRowChecked : styles.confirmRowPending)
        }
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
        />
        {p.wearCheckboxLabel}
      </label>
      {hasName ? null : (
        <p className={styles.nameRequired}>{p.nameRequiredNote}</p>
      )}
      <button
        type="button"
        className={styles.submitButton}
        disabled={!checked || !hasName || confirming}
        onClick={() => onConfirm(checked)}
      >
        {confirming ? p.lockingInButton : p.lockInButton}
      </button>
      <button
        type="button"
        className={styles.linkButton}
        onClick={onBack}
        disabled={confirming}
      >
        {p.chooseDifferentOutfitButton}
      </button>
    </div>
  );
}

function ResultScreen({ appearance, wardrobeChannels, channels, teamName }) {
  const navigate = useNavigate();
  return (
    <div className={styles.resultScreen}>
      <h2>{p.resultHeading}</h2>
      {/* Signed up through the game link, so no team yet: say what happens
          next, or this screen reads as the end of the story and the door
          scan comes as a surprise (roadmap R15). */}
      {teamName ? null : (
        <p className={styles.nextStepNote}>{p.nextStepNote}</p>
      )}
      <div className={styles.resultGarments}>
        {wardrobeChannels.map((name) => {
          const channel = channels.find((c) => c.name === name);
          const label = appearance ? appearance[name] : null;
          return (
            <div key={name} className={styles.resultGarment}>
              <Swatch hex={channel.hex[label]} label={label} size="large" />
              <div>
                <div className={styles.resultChannelName}>
                  {channelLabel(name)}
                </div>
                <div>{label}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p className={styles.finalNote}>{p.finalNote}</p>
      {/* The way in by hand. The poll below sends a player here on its own
          once the game starts, but a player who picked early has nothing to
          do until then except grant the camera and location permissions the
          game needs - and that is what / asks for while it waits, so make
          going there something they can do now rather than only something
          that happens to them later. */}
      <button
        className={`${styles.submitButton} ${styles.enterGameButton}`}
        onClick={() => navigate("/")}
        type="button"
      >
        {p.goToGameButton}
      </button>
      <p className={styles.enterGameNote}>{p.enterGameNote}</p>
      <p>{p.askAdminNote}</p>
    </div>
  );
}

// A running "how far through joining am I" bar, shown above every screen of
// the flow. This is the same nudge idea as item 12's fix direction one level
// up: rather than only warning at the confirm step that the player isn't
// done, show it continuously, so stopping early always looks unfinished
// rather than looking like a natural resting point.
function JoinProgressBar({ percent }) {
  return (
    <div className={styles.progressBar}>
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-label={p.joinProgressAriaLabel}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={
            styles.progressFill +
            (percent >= 100 ? " " + styles.progressFillDone : "")
          }
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className={styles.progressPercent}>
        {p.progressPercent(percent)}
      </span>
    </div>
  );
}

// Where the picker keeps its place. Everything the player has done short of
// claiming an outfit - which colours they own, which page of options they are
// reading, which outfit they are being asked to confirm - lives in the query
// string beside the join code, so a reload (or the browser reaping a
// backgrounded tab) comes back to the same screen instead of an empty
// wardrobe form. Query parameters rather than path segments because the join
// code is already one, and because the step is derivable from them: options
// are shown once there is a page, the confirm screen once there is an outfit.
function wardrobeParam(channelName) {
  return `w_${channelName}`;
}

// The nudge: default every colour to ticked, not empty. A player who never
// touches this step still gets offered outfits at all - ranked
// canonical-first, so the ones near the top of the list are the ones we'd
// actually prefer they land on - rather than the thin, often non-canonical
// set an empty (or barely-ticked) wardrobe produces. Ticking is then framed
// as narrowing down for a better match, not building up from nothing.
//
// An absent parameter is therefore that default, and an empty one ("w_tshirt=")
// is a player who unticked the lot - two different things, which is why the
// absence is not simply written out as "".
function wardrobeFromParams(searchParams, joinData) {
  const wardrobe = {};
  for (const name of joinData.wardrobe_channels) {
    const channel = joinData.channels.find((c) => c.name === name);
    const labels = channel ? channel.labels : [];
    const picked = searchParams.get(wardrobeParam(name));
    wardrobe[name] =
      picked === null
        ? [...labels]
        : // Filtered through the channel's own labels, so a hand-edited URL
          // cannot post a colour the scheme has never heard of - and so the
          // order is the palette's rather than the order they were tapped.
          labels.filter((label) => picked.split(",").includes(label));
  }
  return wardrobe;
}

// An outfit named in the URL, as something stable enough to find it again by
// once the option list has been re-fetched. Only ever compared, never parsed
// back - the option itself comes from the list.
function outfitKey(appearance) {
  return Object.keys(appearance)
    .sort()
    .map((name) => `${name}-${appearance[name]}`)
    .join(".");
}

function PickOutfitForm({
  code,
  joinData,
  onPicked,
  onError,
  onChoosingChange,
  onProgressChange,
}) {
  const [searchParams] = useSearchParams();
  const patchSearchParams = usePatchSearchParams();

  const wardrobe = useMemo(
    () => wardrobeFromParams(searchParams, joinData),
    [searchParams, joinData],
  );
  const pageParam = searchParams.get("page");
  const page = pageParam === null ? null : Math.max(0, Number(pageParam) || 0);
  const relaxed = searchParams.get("relaxed") === "1";
  const showingAll = searchParams.get("all") === "1";
  const selectedKey = searchParams.get("outfit");

  const [optionsResult, setOptionsResult] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // The confirm screen is the URL's outfit found in the list that is loaded,
  // so there is one place the current step is written down. An outfit that
  // isn't in the list any more - somebody else claimed it while this tab was
  // shut - drops back to the list rather than offering something unclaimable.
  const selectedOption =
    (selectedKey &&
      optionsResult &&
      optionsResult.options.find(
        (option) => outfitKey(option.appearance) === selectedKey,
      )) ||
    null;
  // Lifted out of ConfirmScreen (rather than that component's own state) so
  // the progress bar below can see it too.
  const [checked, setChecked] = useState(false);
  // join_options only reports the name as it was on load, and NameEntry posts
  // set_name without telling anyone - so track it here, since the confirm
  // step is gated on it.
  const [name, setName] = useState(
    joinData.you && joinData.you.name ? joinData.you.name : null,
  );

  // The header's "tell us what else you'll be wearing" prompt is an
  // instruction, and on the confirm screen there is nothing left to tell -
  // the player is being asked to approve a specific outfit. Let the parent,
  // which owns the header, know when we are on that screen.
  useEffect(() => {
    onChoosingChange(selectedOption !== null);
  }, [selectedOption, onChoosingChange]);

  // A fresh outfit (or none at all) starts unconfirmed - same reset a full
  // unmount/remount of ConfirmScreen used to give this for free. Keyed on the
  // URL's outfit rather than the option object, whose identity changes every
  // time the list behind it is re-fetched: unticking somebody's consent box
  // while they are reading it is exactly the silent failure this page exists
  // to avoid.
  useEffect(() => {
    setChecked(false);
  }, [selectedKey]);

  const wardrobeChannels = joinData.wardrobe_channels;
  const showingOptions = optionsResult && optionsResult.total > 0;

  // The progress bar's own five rungs: name given, outfits found, one
  // tapped, consent ticked, locked in (that last one only the parent can
  // know, since claiming happens above this component). Whole-flow rather
  // than per-screen, so backing up (Change what I own, Choose a different
  // outfit, a 409) honestly drops the bar back down instead of it only ever
  // climbing.
  useEffect(() => {
    let percent = 0;
    if (name) percent = 20;
    if (showingOptions) percent = 40;
    if (selectedOption) percent = 60;
    if (checked) percent = 80;
    onProgressChange(percent);
  }, [name, showingOptions, selectedOption, checked, onProgressChange]);

  // Back to the wardrobe form: the next list is a fresh one, so it starts
  // nudging again rather than inheriting a previous "show me the rest".
  const closedOptions = { page: null, relaxed: null, all: null, outfit: null };

  const reopenWardrobe = () => {
    patchSearchParams(closedOptions, { replace: false });
  };

  const toggleColour = (channelName, label) => {
    const current = wardrobe[channelName] || [];
    const next = current.includes(label)
      ? current.filter((l) => l !== label)
      : [...current, label];
    // Replaced rather than pushed: a dozen taps on swatches should not be a
    // dozen presses of the back button to get off this page.
    patchSearchParams({
      ...closedOptions,
      [wardrobeParam(channelName)]: next.join(","),
    });
  };

  const fetchOptions = useCallback(
    async (relaxed, page) => {
      setOptionsLoading(true);
      try {
        const result = await postJSON("outfit_options", {
          data: code,
          wardrobe,
          relaxed,
          page,
        });
        setOptionsResult(result);
      } catch (e) {
        onError(e.message);
      } finally {
        setOptionsLoading(false);
      }
    },
    [code, wardrobe, onError],
  );

  // The list follows the URL: asking for outfits, turning a page and reloading
  // the tab are all the same act of putting a page number in the query string.
  // Guarded on what was last asked for, because the other things that live in
  // the query string (the chosen outfit, "show me the rest") must not re-post
  // a list the player is already reading - least of all out from under the
  // confirm screen.
  const lastFetchKey = useRef(null);
  const wardrobeKey = JSON.stringify(wardrobe);

  useEffect(() => {
    if (page === null) {
      lastFetchKey.current = null;
      setOptionsResult(null);
      return;
    }
    const key = `${page}|${relaxed}|${wardrobeKey}`;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    fetchOptions(relaxed, page);
  }, [page, relaxed, wardrobeKey, fetchOptions]);

  const claimOption = useCallback(
    async (option, confirmed) => {
      setClaiming(true);
      try {
        const row = await postJSON("pick_outfit", {
          data: code,
          wardrobe,
          appearance: option.appearance,
          confirmed,
        });
        onPicked(row);
      } catch (e) {
        onError(e.message);
        // Somebody else took this outfit between it being offered and being
        // claimed: forget what was fetched so dropping the outfit from the URL
        // re-asks for the page, rather than re-offering the one just refused.
        if (e.status === 409) lastFetchKey.current = null;
        patchSearchParams({ outfit: null });
      } finally {
        setClaiming(false);
      }
    },
    [code, wardrobe, onPicked, onError, patchSearchParams],
  );

  // Always shown, on every step including the confirm screen - a name
  // already known from an earlier session is worth reviewing (it stays
  // editable), and one not yet given is worth asking for again, since the
  // confirm step refuses to claim an outfit without it.
  const nameEntry = (
    <NameEntry
      user={{ name }}
      className={styles.nameEntry}
      onNameSet={setName}
    />
  );

  if (selectedOption) {
    return (
      <>
        {nameEntry}
        <ConfirmScreen
          option={selectedOption}
          wardrobeChannels={wardrobeChannels}
          channels={joinData.channels}
          confirming={claiming}
          hasName={Boolean(name)}
          checked={checked}
          onCheckedChange={setChecked}
          onConfirm={(confirmed) => claimOption(selectedOption, confirmed)}
          onBack={() => patchSearchParams({ outfit: null }, { replace: false })}
        />
      </>
    );
  }

  const showAreYouSure =
    optionsResult && optionsResult.total === 0 && !optionsResult.relaxed;
  const totalPages = optionsResult
    ? Math.max(1, Math.ceil(optionsResult.total / optionsResult.page_size))
    : 0;

  // Every option is pickable, but the canonical ones are the ones the code
  // is built out of - offering the rest alongside them invites a player to
  // spend identification accuracy on a colour they like the look of. So only
  // the canonical ones are shown until the player asks for the others, which
  // also hides the pagination: a good outfit is never more than a tap away,
  // and the long tail takes a deliberate one. When the wardrobe supports no
  // canonical outfit at all there is nothing to nudge towards, so the full
  // list stands as it is rather than leaving an empty page.
  const pageOptions = optionsResult ? optionsResult.options : [];
  const canonicalOptions = pageOptions.filter((option) => option.is_canonical);
  const nudging = !showingAll && canonicalOptions.length > 0;
  const visibleOptions = nudging ? canonicalOptions : pageOptions;
  const hasOthers =
    canonicalOptions.length < pageOptions.length || totalPages > 1;
  const recommendedKeys = new Set(
    (optionsResult && optionsResult.page === 0
      ? bestOptions(pageOptions)
      : []
    ).map((option) => optionDescription(option, wardrobeChannels)),
  );

  return (
    <>
      {nameEntry}

      {showingOptions ? (
        <WardrobeSummary
          wardrobeChannels={wardrobeChannels}
          wardrobe={wardrobe}
          onChange={reopenWardrobe}
        />
      ) : (
        <>
          <p className={styles.wardrobeIntro}>{p.wardrobeIntro()}</p>

          {wardrobeChannels.map((channelName) => {
            const channel = joinData.channels.find(
              (c) => c.name === channelName,
            );
            if (!channel) return null;
            return (
              <WardrobeChannel
                key={channelName}
                channel={channel}
                selected={wardrobe[channelName] || []}
                onToggle={(label) => toggleColour(channelName, label)}
              />
            );
          })}

          <button
            type="button"
            className={styles.submitButton}
            disabled={optionsLoading}
            onClick={() =>
              patchSearchParams({ page: 0, relaxed: null }, { replace: false })
            }
          >
            {optionsLoading ? p.findingOutfitsButton : p.showMeOutfitsButton}
          </button>
        </>
      )}

      {showAreYouSure ? (
        <div className={styles.emptyState}>
          <p>{p.noOutfitsFoundNote}</p>
          <button
            type="button"
            onClick={() =>
              patchSearchParams({ page: 0, relaxed: 1 }, { replace: false })
            }
          >
            {p.yesImSureButton}
          </button>
        </div>
      ) : null}

      {optionsResult && optionsResult.exhausted ? (
        <p className={styles.exhaustedNote}>{p.exhaustedNote}</p>
      ) : null}

      {showingOptions ? (
        <>
          <OptionsList
            options={visibleOptions}
            recommendedKeys={recommendedKeys}
            wardrobeChannels={wardrobeChannels}
            channels={joinData.channels}
            onPick={(option) =>
              patchSearchParams(
                { outfit: outfitKey(option.appearance) },
                { replace: false },
              )
            }
          />
          {nudging && hasOthers ? (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => patchSearchParams({ all: 1 }, { replace: false })}
            >
              {p.showMoreOutfitsButton}
            </button>
          ) : null}
          {!nudging && totalPages > 1 ? (
            <div className={styles.pagination}>
              <button
                type="button"
                disabled={optionsResult.page <= 0 || claiming}
                onClick={() =>
                  patchSearchParams(
                    { page: optionsResult.page - 1 },
                    { replace: false },
                  )
                }
              >
                {p.previousButton}
              </button>
              <span>{p.pageOf(optionsResult.page + 1, totalPages)}</span>
              <button
                type="button"
                disabled={optionsResult.page + 1 >= totalPages || claiming}
                onClick={() =>
                  patchSearchParams(
                    { page: optionsResult.page + 1 },
                    { replace: false },
                  )
                }
              >
                {p.nextButton}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

// How often to check whether the game has gone active, once an outfit is
// locked in - the same idiom OnboardingView/UserMode uses for its own
// permission recheck, not a full SSE connection.
const GAME_START_POLL_MS = 12000;

function PickOutfit() {
  const query = useQuery();
  const code = query.get("j");
  const navigate = useNavigate();

  const [joinData, setJoinData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pickedRow, setPickedRow] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorVisible, setErrorVisible] = useState(false);
  // Reported up by PickOutfitForm as the player moves through the flow; a
  // locked-in result overrides it to 100% below, since a returning visitor
  // who already has a slot never mounts the form at all.
  const [formProgress, setFormProgress] = useState(0);

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    getJSON("join_options", { data: code })
      .then((data) => {
        if (!cancelled) setJoinData(data);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setErrorVisible(true);
  }, []);

  const alreadyPicked =
    joinData &&
    joinData.you &&
    joinData.you.slot !== null &&
    joinData.you.slot !== undefined;
  const result = pickedRow || (alreadyPicked ? joinData.you : null);
  const progress = result ? 100 : formProgress;

  // Once an outfit is locked in, there's nothing left to do on this page but
  // wait for the game to start - and this page never opens an SSE
  // connection, so without this a player who picked ahead of time and left
  // the tab open would have no way in once the game actually kicked off.
  useEffect(() => {
    if (!result) return undefined;
    let cancelled = false;
    const checkActive = () => {
      sendAPIRequest("user_info", null, "GET", (data) => {
        if (!cancelled && data && data.active) {
          navigate("/");
        }
      });
    };
    // Once immediately, then on the timer: a player who picked before the
    // game started and comes back to their join link afterwards - a reload,
    // or the tab reopened - is already late, and waiting out a poll interval
    // staring at a "locked in" screen is exactly how this page looks like a
    // dead end.
    checkActive();
    const interval = setInterval(checkActive, GAME_START_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [result, navigate]);

  return (
    <div className={styles.outerContainer}>
      <Popup visible={errorVisible} setVisible={setErrorVisible}>
        <p>{errorMessage}</p>
      </Popup>
      <div className={styles.innerContainer}>
        {!code ? (
          <p>{p.noInviteLinkNote}</p>
        ) : loadError ? (
          <p className={styles.errorText}>{loadError}</p>
        ) : !joinData ? (
          <p>{p.loadingNote}</p>
        ) : (
          <>
            <JoinProgressBar percent={progress} />
            <Header
              joinData={joinData}
              showWardrobePrompt={!result && !choosing}
            />
            {result ? (
              <ResultScreen
                appearance={result.effective_appearance}
                wardrobeChannels={joinData.wardrobe_channels}
                channels={joinData.channels}
                teamName={result.team_name}
              />
            ) : (
              <PickOutfitForm
                code={code}
                joinData={joinData}
                onPicked={setPickedRow}
                onError={showError}
                onChoosingChange={setChoosing}
                onProgressChange={setFormProgress}
              />
            )}
          </>
        )}
        <CuriosityFooter />
      </div>
    </div>
  );
}

export default PickOutfit;
